import { Component, OnChanges, SimpleChanges, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SubscriptionApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/subscription/subscription-api';
import { BaseToastComponent } from '../../../@commons/ui-components/toast-popup/base-toast-component';
import { SharedModule } from '../../../@commons/ui-components/shared.module';
import { CheckoutService } from '../../../@commons/@services/front/checkout.service';
import { PremiumService } from '../../../@commons/@services/front/premium.service';

/**
 * Durée d'une période, en jours, pour situer l'avancement.
 *
 * Elle se déduit du canal et de la formule, jamais de la première date d'accès :
 * un abonné qui a commencé par un essai aurait sinon une barre calée sur une
 * période qui n'est plus la sienne.
 */
const PERIOD_DAYS: Record<string, number> = {
  TRIAL: 30,
  MONTHLY: 30,
  YEARLY: 365,
};

/** Miroir de `WILLY_FREE_DAILY_LIMIT` côté serveur, pour dire ce qui change. */
const WILLY_FREE_DAILY_LIMIT = 10;

/**
 * « Mon abonnement » : statut, échéance, annulation et réactivation.
 *
 * L'annulation n'est jamais un piège : elle est accessible en deux clics, et le
 * message dit exactement jusqu'à quand l'accès reste ouvert — fin du mois pour
 * un mensuel, fin de l'année pour un annuel. Tant que la période court, un
 * simple bouton « Réactiver » revient en arrière.
 */
@Component({
  selector: 'vw-subscription-toast',
  imports: [SharedModule, CommonModule, TranslateModule],
  templateUrl: './subscription-toast.component.html',
  providers: [{ provide: SubscriptionApi, useClass: SubscriptionApi }],
})
export class SubscriptionToastComponent
  extends BaseToastComponent
  implements OnChanges
{
  loading = false;
  confirmingCancel = false;
  error = false;

  readonly willyFreeLimit = WILLY_FREE_DAILY_LIMIT;

  readonly premiumService = inject(PremiumService);
  private readonly checkoutService = inject(CheckoutService);
  private readonly translate = inject(TranslateService);

  constructor(private readonly subscriptionApi: SubscriptionApi) {
    super();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen']?.currentValue) {
      this.confirmingCancel = false;
      this.error = false;
      void this.premiumService.refresh();
    }
  }

  get state() {
    return this.premiumService.subscription();
  }

  /** Seul un abonnement Stripe se pilote : un essai ou un cadeau n'a rien à annuler. */
  get isStripe(): boolean {
    return this.state?.provider === 'STRIPE';
  }

  get isCanceled(): boolean {
    return Boolean(this.state?.cancelAtPeriodEnd);
  }

  /** Essai ou mois offerts : rien n'est facturé, rien n'est programmé. */
  get isGifted(): boolean {
    return (
      this.state?.provider === 'TRIAL' || this.state?.provider === 'MANUAL'
    );
  }

  /** Date du premier accès premium, formatée. */
  get sinceLabel(): string {
    const since = this.state?.premiumSince;
    return since ? new Date(since).toLocaleDateString() : '';
  }

  /**
   * Avancement dans la période en cours, en pourcentage.
   *
   * `null` quand la durée est inconnue — des mois offerts peuvent couvrir
   * n'importe quelle durée, et une barre inventée mentirait sur le temps
   * restant.
   */
  get periodProgress(): number | null {
    const days = this.daysLeft;
    if (days === null) return null;
    const window =
      this.state?.provider === 'TRIAL'
        ? PERIOD_DAYS['TRIAL']
        : PERIOD_DAYS[this.state?.plan ?? ''];
    if (!window) return null;
    const elapsed = ((window - days) / window) * 100;
    return Math.min(100, Math.max(0, Math.round(elapsed)));
  }

  /**
   * Faut-il dire ce que l'échéance retire ?
   *
   * Seulement quand l'accès s'arrête vraiment : un abonnement qui se renouvelle
   * n'a rien à perdre, et l'avertir reviendrait à lui vendre ce qu'il paie déjà.
   */
  get showAfterSection(): boolean {
    if (!this.state?.isPremium || !this.state?.currentPeriodEnd) return false;
    return this.isGifted || this.isCanceled;
  }

  /** Un mensuel peut basculer sur l'annuel : deux mois de moins à payer. */
  get canSwitchToYearly(): boolean {
    return this.isStripe && this.state?.plan === 'MONTHLY' && !this.isCanceled;
  }

  /** Jours restants avant l'échéance, pour un essai. */
  get daysLeft(): number | null {
    const end = this.state?.currentPeriodEnd;
    if (!end) return null;
    const diff = new Date(end).getTime() - Date.now();
    return diff > 0 ? Math.ceil(diff / 86_400_000) : 0;
  }

  /** Échéance courante, formatée. */
  get periodEndLabel(): string {
    return this.state?.currentPeriodEnd
      ? new Date(this.state.currentPeriodEnd).toLocaleDateString()
      : '';
  }

  /**
   * Le message de confirmation dit la vérité, et elle diffère selon le canal.
   *
   * Un abonnement payé court jusqu'à l'échéance déjà réglée. Un essai ou des
   * mois offerts s'arrêtent SUR-LE-CHAMP : il n'y a rien à facturer, donc rien
   * à faire courir. Le dire clairement évite de laisser croire qu'on garde ses
   * jours restants.
   */
  get cancelConfirmMessage(): string {
    const date = this.periodEndLabel;
    if (this.isGifted) {
      return this.translate.instant('premium.manage.cancel-confirm-gifted');
    }
    const key =
      this.state?.plan === 'YEARLY'
        ? 'premium.manage.cancel-confirm-yearly'
        : 'premium.manage.cancel-confirm-monthly';
    return this.translate.instant(key, { date });
  }

  /** Libellé du bouton de résiliation, adapté au canal. */
  get cancelLabel(): string {
    return this.isGifted
      ? 'premium.manage.stop-gifted'
      : 'premium.manage.cancel';
  }

  async confirmCancel(): Promise<void> {
    await this.run(() => this.subscriptionApi.cancel());
    this.confirmingCancel = false;
  }

  async reactivate(): Promise<void> {
    await this.run(() => this.subscriptionApi.reactivate());
  }

  async openBilling(): Promise<void> {
    this.error = false;
    try {
      await this.checkoutService.manageBilling();
    } catch {
      this.error = true;
    }
  }

  private async run(action: () => Promise<unknown>): Promise<void> {
    this.loading = true;
    this.error = false;
    try {
      await action();
      await this.premiumService.refresh();
    } catch {
      this.error = true;
    } finally {
      this.loading = false;
    }
  }
}
